import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/infrastructure/lib/utils';
import { formatDateTimeBR } from '@/shared/utils/datetime';
import type { SoulVersion } from '@/features/Knowledge/types/BrainTypes';

interface SoulHistoryListProps {
  versions: SoulVersion[];
  isLoading: boolean;
  /** Versão aberta na apresentação; `null` é a atual. */
  selected: number | null;
  onSelect: (version: number | null) => void;
}

/**
 * Versões da Soul (AB#86817, AC2), numa coluna ao lado da apresentação: escolher
 * uma versão a mostra no lugar do texto atual, de onde o admin pode restaurá-la.
 */
export const SoulHistoryList = ({ versions, isLoading, selected, onSelect }: SoulHistoryListProps) => {
  const { t } = useTranslation();
  const current = versions[0]?.version;

  return (
    <aside className="flex w-72 shrink-0 flex-col border-l bg-muted/20">
      <div className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t('knowledge.soul.historyTitle')}
      </div>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : versions.length === 0 ? (
        <p className="p-3 text-xs text-muted-foreground">{t('knowledge.soul.noHistory')}</p>
      ) : (
        <ul className="flex-1 space-y-0.5 overflow-y-auto p-1">
          {versions.map((version) => {
            const isCurrent = version.version === current;
            const isOpen = (selected ?? current) === version.version;
            return (
              <li key={version.version}>
                <button
                  type="button"
                  onClick={() => onSelect(isCurrent ? null : version.version)}
                  className={cn(
                    'w-full rounded-sm px-2 py-2 text-left hover:bg-accent/50',
                    isOpen && 'border-l-2 border-primary bg-accent pl-[7px]'
                  )}
                >
                  <div className="flex items-center gap-2 text-xs font-medium">
                    {t('knowledge.soul.version', { version: version.version })}
                    {isCurrent && (
                      <Badge variant="secondary" className="text-[10px]">
                        {t('knowledge.soul.current')}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                    {formatDateTimeBR(version.created_at)}{' '}
                    {version.author
                      ? t('knowledge.soul.by', { name: version.author.full_name || version.author.email })
                      : t('knowledge.soul.initialVersion')}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
};
